import { ChevronLeft, ChevronRight, Globe } from "lucide-react";
import {
  cn, LocaleFlag,
  DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator,
} from "@wa/ui";
import { useI18n, useT } from "../../i18n/I18nProvider";
import { platformLocaleLabel, usePlatformLocales, type PlatformLocaleInfo } from "../../lib/platformLocales";
import { MENU_ICON, MENU_ROW, MENU_SEP } from "./menuStyles";
import { Seg } from "./Seg";

/**
 * Above this many languages the account menu stops drawing them side by side.
 *
 * A segmented switch shows every choice at once, which is its whole value and
 * also its ceiling: four flags already push the row wider than the panel, and
 * the platform catalogue can grow to any length (a superadmin adds languages
 * from the admin centre). Three is the widest that still fits.
 */
const SEG_LIMIT = 3;

function baseCode(code: string) {
  return code.split("-")[0] ?? code;
}

/**
 * The languages this hub may switch to.
 *
 * Only those whose ACCOUNT UI has a system translation, which is the same rule
 * the standalone `LanguageMenu` applies - read from one place so the two cannot
 * come to disagree about which languages exist.
 */
function useLocaleOptions(): { catalog: PlatformLocaleInfo[]; options: PlatformLocaleInfo[] } {
  const { locales: catalog } = usePlatformLocales();
  const translated = catalog.filter((l) => l.system?.account);
  const options = translated.length ? translated : catalog.filter((l) => l.code === "et" || l.code === "en");
  return { catalog, options };
}

const ROW_LABEL = "flex min-w-0 flex-1 items-center gap-2.5 whitespace-nowrap text-[12.75px] font-medium";

/**
 * The language row in the account menu's root view.
 *
 * With a short catalogue it is a switch: both or all three languages are on
 * screen, one press changes the page underneath, and the menu stays open. With
 * a long one it becomes a door into a second view of the SAME panel, which is
 * the point of `onOpen`. Not a kit submenu: the menu hangs from the right edge
 * of the header, so a submenu flips to the left, and on a phone there is no
 * room there either - Radix places it off the screen and cannot move it back
 * sideways.
 *
 * Both shapes are kit menu items, so the menu's arrow keys reach them.
 */
export function LanguageRow({ onOpen }: { onOpen: () => void }) {
  const t = useT();
  const { locale, setLocale } = useI18n();
  const { catalog, options } = useLocaleOptions();
  const label = t("common.switchLanguage");

  if (options.length > SEG_LIMIT) {
    return (
      <DropdownMenuItem
        // Where focus lands when the list closes (see AccountMenu).
        data-language-row=""
        // Opens the list in place, so the menu must not close.
        onSelect={(e) => {
          e.preventDefault();
          onOpen();
        }}
        className={cn(MENU_ROW, "pr-2.5")}
      >
        <span className={ROW_LABEL}>
          <Globe className={MENU_ICON} />
          {label}
        </span>
        <span className="flex shrink-0 items-center gap-1.5 text-[11.5px] font-bold text-muted-foreground">
          <LocaleFlag locale={baseCode(locale)} className="w-[15px]" />
          {baseCode(locale).toUpperCase()}
        </span>
        <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
      </DropdownMenuItem>
    );
  }

  return (
    <div className="flex items-center gap-2.5 py-1 pl-3 pr-1.5">
      <span className={ROW_LABEL}>
        <Globe className={MENU_ICON} />
        {label}
      </span>
      <Seg
        label={label}
        value={locale}
        onChange={setLocale}
        options={options.map((opt) => ({
          value: opt.code,
          title: platformLocaleLabel(catalog, opt.code),
          label: (
            <>
              <LocaleFlag locale={baseCode(opt.code)} className="w-[15px]" />
              {baseCode(opt.code).toUpperCase()}
            </>
          ),
        }))}
      />
    </div>
  );
}

/**
 * The account menu's second view: the full language list, in place of the root
 * rows rather than on top of them.
 *
 * Picking a language returns to the root view instead of closing the menu, for
 * the same reason the short-catalogue switch stays open: you see what changed
 * without reopening anything to check.
 */
export function LanguageList({ onBack }: { onBack: () => void }) {
  const t = useT();
  const { locale, setLocale } = useI18n();
  const { catalog, options } = useLocaleOptions();

  return (
    <>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          onBack();
        }}
        className={cn(MENU_ROW, "gap-2 px-2.5 font-semibold")}
      >
        <ChevronLeft className={MENU_ICON} />
        {t("common.switchLanguage")}
      </DropdownMenuItem>

      <DropdownMenuSeparator className={MENU_SEP} />

      {/* The list is what scrolls, not the panel: the way back has to stay put
          while the languages move under it. */}
      <div className="max-h-[min(46vh,320px)] overflow-y-auto [scrollbar-width:thin]">
        <DropdownMenuRadioGroup
          value={locale}
          onValueChange={(code) => {
            setLocale(code);
            onBack();
          }}
        >
          {options.map((opt) => (
            <DropdownMenuRadioItem
              key={opt.code}
              value={opt.code}
              onSelect={(e) => e.preventDefault()}
              className={cn(MENU_ROW, "pl-8 data-[state=checked]:font-semibold data-[state=checked]:text-primary")}
            >
              <LocaleFlag locale={baseCode(opt.code)} className="w-5 shrink-0" />
              <span className="flex-1 truncate">{platformLocaleLabel(catalog, opt.code)}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </div>
    </>
  );
}
